import HeaderBar from "../components/layout/Headerbar";
import NavBar from "../components/layout/NavBar";
import { useApiCustomPoiCollections, apiDeleteCustomPoiCollection, apiPublishCustomPoiCollection } from "../api/customPoi";
// CustomPoiCollectionModel is not used in this component
import { useNavigate } from "react-router-dom";
import LargeButton from "../components/input/LargeButton";
import SmallButton from "../components/input/SmallButton";
import { useState } from "react";
import addIcon from "../assets/add.png";
import collectionIcon from "../assets/location.png";
import "./CustomPoiList.css";

function CustomPoiCollectionList() {
  const customPoiCollections = useApiCustomPoiCollections();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState<string>("all");

  const isUserForbidden =
    customPoiCollections.error?.response?.status === 403 ? true : false;

  const filteredCollections = customPoiCollections.data?.filter((collection) => {
    if (filterStatus === "all") return true;
    return collection.status === filterStatus;
  }) || [];

  const handleDelete = async (id: number) => {
    const isConfirmed = window.confirm(
      "***WARNING***\n\nYou are about to delete a Custom POI Collection. This action cannot be undone. Are you sure you want to proceed?"
    );
    if (!isConfirmed) return;

    try {
      await apiDeleteCustomPoiCollection(id);
      customPoiCollections.refresh();
    } catch (error) {
      console.error("Failed to delete Custom POI Collection:", error);
    }
  };

  const handlePublish = async (id: number) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to publish this Custom POI Collection? It will become visible to users."
    );
    if (!isConfirmed) return;

    try {
      await apiPublishCustomPoiCollection(id);
      customPoiCollections.refresh();
    } catch (error) {
      console.error("Failed to publish Custom POI Collection:", error);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "published":
        return "status-published";
      case "draft":
        return "status-draft";
      case "archived":
        return "status-archived";
      default:
        return "";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="app">
      <HeaderBar>
        <img
          src={collectionIcon}
          alt="Custom POI Collections"
          height={32}
          style={{ display: "inline-block", marginLeft: 20, marginTop: 25 }}
        />
        <h1
          style={{
            display: "inline-block",
            marginLeft: 8,
            verticalAlign: "top",
            paddingTop: 13.5,
            fontWeight: 600,
          }}
        >
          Custom POI Collections
        </h1>
        {!isUserForbidden && (
          <div
            style={{
              display: "inline-flex",
              right: 0,
              position: "absolute",
              boxSizing: "border-box",
              padding: 15,
            }}
          >
            <LargeButton
              text="Add new Collection"
              onClick={() => {
                navigate("/custom-poi-collections/new");
              }}
              icon={addIcon}
              color={"#F0EDE0"}
            />
          </div>
        )}
      </HeaderBar>

      <NavBar selectedIndex={7} isUserForbidden={isUserForbidden} />

      <div className="content" style={{ paddingLeft: 16, paddingRight: 16 }}>
        {!isUserForbidden ? (
          <div>
            <p
              style={{
                marginLeft: 4,
                paddingBottom: 28,
                maxWidth: 800,
                fontSize: 18,
              }}
            >
              Create and manage collections of Custom POIs. Group related POIs together for better organization and user experience.
            </p>

            {/* Status Filter */}
            <div className="filter-section">
              <label htmlFor="status-filter">Filter by status:</label>
              <select
                id="status-filter"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="status-filter"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
              <span className="poi-count">
                Showing {filteredCollections.length} collection{filteredCollections.length !== 1 ? 's' : ''}
              </span>
            </div>

            <table className="poi-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Persona</th>
                  <th>POI Count</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCollections.map((collection) => (
                  <tr key={collection.id}>
                    <td>
                      <div className="poi-title-cell">
                        <strong>{collection.title}</strong>
                        <div className="poi-description-preview">
                          {collection.description && collection.description.length > 100
                            ? `${collection.description.substring(0, 100)}...`
                            : collection.description || "No description"}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="persona-cell">
                        {collection.persona && (
                          <>
                            <img
                              src={collection.persona.pictureUrl}
                              alt={collection.persona.name}
                              className="persona-avatar"
                            />
                            <span>{collection.persona.name}</span>
                          </>
                        )}
                        {!collection.persona && (
                          <span className="no-persona">General</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="poi-count-badge">
                        {collection.customPois?.length || 0} POI{collection.customPois?.length !== 1 ? 's' : ''}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${getStatusBadgeClass(collection.status)}`}>
                        {collection.status}
                      </span>
                    </td>
                    <td>
                      {formatDate(collection.createdAt)}
                      {collection.publishedAt && (
                        <div className="published-date">
                          Published: {formatDate(collection.publishedAt)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <SmallButton
                          text="Edit"
                          icon={null}
                          isOutlineOnly={true}
                          onClick={() => navigate(`/custom-poi-collections/${collection.id}`)}
                          className="poi-btn"
                        />
                        {collection.status === 'draft' && (
                          <SmallButton
                            text="Publish"
                            icon={null}
                            isOutlineOnly={false}
                            onClick={() => handlePublish(collection.id)}
                            className="poi-btn publish-btn"
                          />
                        )}
                        <SmallButton
                          text="Delete"
                          icon={null}
                          isOutlineOnly={true}
                          onClick={() => handleDelete(collection.id)}
                          className="poi-btn delete-btn"
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredCollections.length === 0 && (
              <div className="empty-state">
                <p>No Custom POI Collections found.</p>
                <LargeButton
                  text="Create your first collection"
                  onClick={() => navigate("/custom-poi-collections/new")}
                  icon={addIcon}
                  color={"#F0EDE0"}
                />
              </div>
            )}
          </div>
        ) : (
          <div
            className="grid-item"
            style={{
              height: "auto",
              margin: "auto",
            }}
          >
            <div className="host-image">
              <img
                src="https://d1m9alagf2gv12.cloudfront.net/persona/placeholder.jpg"
                alt="access denied"
                height={"100%"}
                width={"100%"}
              />
            </div>
            <h1
              title="Insufficient Permissions"
              style={{
                maxLines: 1,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Insufficient Permissions
            </h1>
            <p>Please contact your administrator for access.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomPoiCollectionList;
